/**
 * Pick Faces — the three storage systems side by side, each a dot grid filling from the bottom
 * like a rack filling from the floor: correctly slotted at the bottom, mis-slotted in red, free
 * space on top, added capacity as dashed outlines above (build handover §5).
 *
 * The three systems and their baseline/added face counts are MOCK (`scenarios/mock.ts`) — no
 * feed in this data names which physical machine a pick face belongs to. Which SKU sits where
 * is also mock (`assignPlacements`), but it's the SAME per-SKU assignment the scatter tags its
 * points with, and levers 1–3 remove a SKU from its placement here the moment `buildClaimMap`
 * claims it — so a released SKU stops occupying its face in BOTH sections at once.
 */

import { useEffect, useRef, useState } from "react";

import type { LeverCause, ScenarioContext, ScenarioParams, Sku } from "@/lib/data";
import { cn } from "@/lib/utils";

import { FLOOR_ENVELOPE_M2, type Placement, systemFigures, type SystemKey, SYSTEMS } from "./mock";
import type { FilterState, ScenariosAction } from "./state";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

export function PickFaces({
  skus,
  placements,
  claim,
  draft,
  ctx,
  filter,
  dispatch,
}: {
  skus: readonly Sku[];
  placements: ReadonlyMap<number, Placement>;
  claim: ReadonlyMap<number, LeverCause>;
  draft: ScenarioParams;
  ctx: ScenarioContext;
  filter: FilterState;
  dispatch: (a: ScenariosAction) => void;
}) {
  const figures = systemFigures(skus, placements, claim, draft, ctx);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [facesPerDot, setFacesPerDot] = useState(60);
  const maxFaces = Math.max(...figures.map((f) => f.faces));

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) {
      return;
    }
    const measure = () => {
      const box = el.getBoundingClientRect();
      if (box.height < 8 || box.width < 8) {
        return;
      }
      const cols = Math.max(1, Math.floor(box.width / 3 / 7));
      const rows = Math.max(3, Math.floor((box.height - 60) / 7));
      let per = Math.ceil(maxFaces / Math.max(1, cols * rows));
      per = Math.max(5, Math.ceil(per / 5) * 5);
      setFacesPerDot((prev) => (prev === per ? prev : per));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxFaces]);

  const totalFaces = ctx.model.capd.ceil + figures.reduce((a, f) => a + f.added, 0);
  const currentYear = ctx.model.years?.[0] ?? 2026;
  const floorLeft =
    FLOOR_ENVELOPE_M2 - draft.capUnits * (draft.capSystem ? sysM2(draft.capSystem) : 0);

  return (
    <div className="section" id="sec-pf">
      <div className="section-head">
        <div className="section-title">Pick Faces</div>
        {/* Fix 5 (Option B): the panels always show the CURRENT physical position — where SKUs
            sit today — not the year selected in the capacity table above. Name it so a reader
            never mistakes these for a forward year's placement. */}
        <div className="pf-year">current position · {currentYear}</div>
      </div>
      <div className="section-body pf-body">
        <div className="pf-wrap" ref={wrapRef}>
          {figures.map((f) => {
            const dTot = Math.max(1, Math.round(f.faces / facesPerDot));
            const dOcc = Math.round(f.occ / facesPerDot);
            const dMis = Math.round(f.mis / facesPerDot);
            const dAdd = Math.round(f.added / facesPerDot);
            const dFree = Math.max(0, dTot - dOcc - dMis - dAdd);
            const sel = filter?.sys === f.key;
            const misSel = sel && filter?.mis;
            // Minibar segment widths. Normally each is its share of the system's faces (they sum
            // to ≤ 100). When in-use exceeds faces (over-subscription, which Fix 2 now surfaces),
            // divide by in-use instead so occ and mis stay in TRUE proportion and fill the bar —
            // clamping each to faces would understate the mis-slot share (it'd get clipped).
            const used = f.occ + f.mis;
            const overSub = used > f.faces;
            const denom = overSub ? used : f.faces;
            const occWidth = denom > 0 ? (f.occ / denom) * 100 : 0;
            const misWidth = denom > 0 ? (f.mis / denom) * 100 : 0;
            return (
              <div key={f.key} className={cn("pf-sys", sel && "sel")} data-spot={`pf-${f.key}`}>
                <div
                  className="pf-head"
                  onClick={() =>
                    dispatch({
                      type: "filter",
                      filter:
                        filter?.sys === f.key && !filter.mis ? null : { sys: f.key, mis: false },
                    })
                  }
                >
                  <div className="pf-name">
                    {f.name}
                    <b>{fmt(f.faces)}</b>
                  </div>
                  <div className="pf-aff">
                    {f.affinity}
                    {f.added ? ` · +${draft.capUnits}` : ""}
                  </div>
                </div>
                <div className="pf-field">
                  {Array.from({ length: dOcc }).map((_, i) => (
                    <span key={`o${i}`} className="pf-dot occ" />
                  ))}
                  {Array.from({ length: dMis }).map((_, i) => (
                    <span key={`m${i}`} className="pf-dot mis" />
                  ))}
                  {Array.from({ length: dFree }).map((_, i) => (
                    <span key={`f${i}`} className="pf-dot free" />
                  ))}
                  {Array.from({ length: dAdd }).map((_, i) => (
                    <span key={`a${i}`} className="pf-dot added" />
                  ))}
                </div>
                <div className="pf-minibar">
                  <i className="occ" style={{ width: `${occWidth.toFixed(2)}%` }} />
                  <i className="mis" style={{ width: `${misWidth.toFixed(2)}%` }} />
                </div>
                <div className="pf-stats">
                  <div>
                    <b>{fmt(f.occ + f.mis)}</b>
                    <span>in use</span>
                  </div>
                  <div
                    className={cn("mis", f.mis === 0 && "clean", misSel && "hot")}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (f.mis === 0) {
                        return;
                      }
                      dispatch({
                        type: "filter",
                        filter:
                          filter?.sys === f.key && filter.mis ? null : { sys: f.key, mis: true },
                      });
                    }}
                  >
                    <b>{f.mis > 0 ? fmt(f.mis) : "none"}</b>
                    <span>mis-slot</span>
                  </div>
                  {/* Fix 2: free is `faces − in-use`, never clamped to 0. A negative value is
                      real over-subscription — render it as "over by N" in the mis-slot red, so an
                      overflow can never hide behind a fake zero again. */}
                  <div className={cn(f.free < 0 && "over")}>
                    <b>{f.free < 0 ? fmt(-f.free) : fmt(f.free)}</b>
                    <span>{f.free < 0 ? "over" : "free"}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="pf-foot">
          <div className="pf-key">
            <span>
              <i className="pf-sw occ" />
              slotted correctly
            </span>
            <span>
              <i className="pf-sw mis" />
              mis-slotted
            </span>
            <span>
              <i className="pf-sw free" />
              free
            </span>
            <span>
              <i className="pf-sw added" />
              added
            </span>
          </div>
          <div className="pf-foot-r">
            <span>
              <b>{fmt(Math.max(0, floorLeft))}</b> m² envelope free
            </span>
            <span>
              <b>{fmt(totalFaces)}</b> faces = ceiling
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function sysM2(key: SystemKey): number {
  return SYSTEMS.find((s) => s.key === key)?.addM2 ?? 0;
}
